import React from "react";
import styled from "styled-components";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CLink } from "@carrefour/design-system-components-react";
import NPSCard from "../../NPSCard";
import { useStores } from "../../../context/storesContext";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import { Range } from "../../../utils/range";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import MrclText from "../../marcel/MrclText";

const CardWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;

const PerformanceHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const DetailLink = styled(CLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #0970e6;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled(MrclText)`
  text-align: left;
  align-self: flex-start;
  font-weight: 700;
  font-size: var(--font-size-body-m, 16px);
  line-height: var(--line-height-body-m, 24px);
  letter-spacing: var(--letter-spacing-normal, normal);
  color: #374151;
`;

const NpsCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  > * {
    border: 1px solid #e5e7eb;
    height: 100%;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 768px),
    only screen and (orientation: landscape) and (max-height: 700px) and (max-width: 1024px),
    only screen and (max-height: 560px) and (orientation: landscape),
    only screen and (max-width: 960px) and (orientation: landscape) {
    grid-template-columns: 1fr;
  }
  `;

  const NpsVerbatims: React.FC = () => {
  const navigate = useNavigate();
  const { selectedStore } = useStores();
  const { isKpiExists } = useKpiVisibility();
  const { t } = useTranslation();

  const hasStoreKpi = isKpiExists("Client", "NpsStore");
  const hasDriveKpi = isKpiExists("Client", "NpsDrive");

  const handleCardClick = () => {
    window.scrollTo(0, 0);
    navigate(`/client?npsStore=${Range.PreviousDay}&npsDrive=${Range.CurrentWeek}`);
  };

  // NPS STORE (default J-1)
  const { data: npsStoreData, error: npsStoreError } = useSWR(
    () =>
      selectedStore?.storeKey && hasStoreKpi
        ? [
            `/nps?range=${Range.PreviousDay}&origin=${"STORE"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  // NPS DRIVE (default Current Week)
  const { data: npsDriveData, error: npsDriveError } = useSWR(
    () =>
      selectedStore?.storeKey && hasDriveKpi
        ? [
            `/nps?range=${Range.CurrentWeek}&origin=${"DRIVE"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  if (!hasStoreKpi && !hasDriveKpi) {
    return null;
  }

  return (
    <CardWrapper>
      <PerformanceHeaderRow>
        <Title>NPS</Title>
        <DetailLink onClick={handleCardClick}>
          Voir le détail
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </DetailLink>
      </PerformanceHeaderRow>
      <NpsCardsContainer data-testid="nps-grid-container">
        {hasStoreKpi && (
          <NPSCard
            isLoading={!npsStoreData}
            isUnavailable={
              !!npsStoreError ||
              npsStoreData === null ||
              npsStoreData?.neutrals === null
            }
            npsData={npsStoreData}
            hideRangeSelect={true}
            rangeLabel={t("common.j_1")}
            title={t("client.nps_magasin")}
          />
        )}
        {hasDriveKpi && (
          <NPSCard
            isLoading={!npsDriveData}
            isUnavailable={
              !!npsDriveError ||
              npsDriveData === null ||
              npsDriveData?.neutrals === null
            }
            npsData={npsDriveData}
            hideRangeSelect={true}
            rangeLabel={t("common.short_semaine_cours")}
            title={t("client.nps_drive")}
          />
        )}
      </NpsCardsContainer>
    </CardWrapper>
  );
};

export default NpsVerbatims;
